import React, { useState, useEffect } from 'react';
import { FiSave, FiRotateCcw, FiMail, FiSend, FiServer } from 'react-icons/fi';
import adminSettingsService from '../../../services/adminSettingsService';
import { Loading } from '../../../components/UI/Loading';

const EmailSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const result = await adminSettingsService.getEmailSettings();
      if (result.success) {
        setSettings(result.data);
        setOriginalSettings(result.data);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al cargar configuraciones' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const result = await adminSettingsService.updateEmailSettings(settings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Configuración actualizada correctamente' });
        setOriginalSettings(settings);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al actualizar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Por favor ingrese un email para la prueba' });
      return;
    }

    setIsTesting(true);
    setMessage(null);
    try {
      const result = await adminSettingsService.testEmailConfiguration(testEmail);
      if (result.success) {
        setMessage({ type: 'success', text: `Email de prueba enviado a ${testEmail}` });
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al enviar email de prueba' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsTesting(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('¿Desea descartar los cambios realizados?')) {
      setSettings(originalSettings);
      setMessage(null);
    }
  };

  if (isLoading) {
    return <Loading message="Cargando configuraciones de email..." />;
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  return (
    <div className="settings-page">
      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Configuración SMTP */}
      <div className="settings-section">
        <div className="section-header">
          <FiServer size={24} />
          <div>
            <h3 className="section-title">Servidor SMTP</h3>
            <p className="section-description">Configuración del servidor de correo saliente</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Host SMTP</label>
            <input
              type="text"
              className="form-input"
              value={settings?.smtp_host || ''}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Puerto</label>
            <input
              type="number"
              className="form-input"
              value={settings?.smtp_port || 587}
              onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
              min="1"
              max="65535"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Usuario SMTP</label>
            <input
              type="text"
              className="form-input"
              value={settings?.smtp_user || ''}
              onChange={(e) => handleChange('smtp_user', e.target.value)}
              placeholder="notifications@betelnatural.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Contraseña SMTP</label>
            <input
              type="password"
              className="form-input"
              value={settings?.smtp_password || ''}
              onChange={(e) => handleChange('smtp_password', e.target.value)}
              placeholder="••••••••"
            />
            <small className="form-helper">Use contraseña de aplicación para Gmail</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.smtp_tls || true}
                onChange={(e) => handleChange('smtp_tls', e.target.checked)}
              />
              <span>Usar TLS/SSL</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.enable_email_sending || true}
                onChange={(e) => handleChange('enable_email_sending', e.target.checked)}
              />
              <span>Habilitar envío de emails</span>
            </label>
          </div>
        </div>
      </div>

      {/* Configuración de Remitente */}
      <div className="settings-section">
        <div className="section-header">
          <FiMail size={24} />
          <div>
            <h3 className="section-title">Información del Remitente</h3>
            <p className="section-description">Datos que aparecen en los emails enviados</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Email del Remitente</label>
            <input
              type="email"
              className="form-input"
              value={settings?.from_email || ''}
              onChange={(e) => handleChange('from_email', e.target.value)}
              placeholder="noreply@betelnatural.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Nombre del Remitente</label>
            <input
              type="text"
              className="form-input"
              value={settings?.from_name || ''}
              onChange={(e) => handleChange('from_name', e.target.value)}
              placeholder="Betel Natural"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email de Respuesta</label>
            <input
              type="email"
              className="form-input"
              value={settings?.reply_to_email || ''}
              onChange={(e) => handleChange('reply_to_email', e.target.value)}
              placeholder="info@betelnatural.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Máx. Destinatarios por Email</label>
            <input
              type="number"
              className="form-input"
              value={settings?.max_recipients_per_email || 50}
              onChange={(e) => handleChange('max_recipients_per_email', parseInt(e.target.value))}
              min="1"
              max="100"
            />
          </div>
        </div>
      </div>

      {/* Probar Configuración */}
      <div className="settings-section">
        <div className="section-header">
          <FiSend size={24} />
          <div>
            <h3 className="section-title">Probar Configuración</h3>
            <p className="section-description">Enviar un email de prueba para verificar la configuración</p>
          </div>
        </div>

        <div className="test-email-container">
          <input
            type="email"
            className="form-input"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="admin@betelnatural.com"
          />
          <button
            className="btn btn--primary"
            onClick={handleTestEmail}
            disabled={isTesting || !settings?.enable_email_sending}
          >
            <FiSend size={16} />
            {isTesting ? 'Enviando...' : 'Enviar Email de Prueba'}
          </button>
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="settings-actions">
        <button
          className="btn btn--outline"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          <FiRotateCcw size={16} />
          Descartar Cambios
        </button>

        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <FiSave size={16} />
          {isSaving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

export default EmailSettings;
